// src/store/sprintStore.ts
/**
 * .tuMetrics {"tu1": 0.86, "tu2": 0.88, "tu3": 7, "notes": "Sprint store; realtime UPDATE keeps tasks; projectId null clears", "tuVersion": "2.5"}
 * .help "Sprint state store for list loading, team filtering, realtime sync, and sprint lifecycle actions."
 */
import { create } from 'zustand';
import { Sprint } from '../types';
import useUserStore from './userStore';
import useFeedStore from './feedStore';
import useCardStore from './cardStore';
import useFollowStore from './followStore';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { format } from 'date-fns';
import {
  mapSprintFromDB,
  mapSprintToDB,
  fetchSprints,
  insertSprint,
  updateSprintById,
  deleteSprintById,
} from '@/lib/supabase/queries/sprint-queries';
import { followRefsFromSprint } from '@/features/feed/followMatch';

interface SprintState {
  sprints: Sprint[];
  isLoading: boolean;
  error: string | null;
  selectedTeamId: string | null;
  sprintDisplayMode: 'list' | 'board';
  selectedSprintId: string | null;
  lastFetchedAt: number | null; // Timestamp of last fetch for staleness check
  realtimeChannel: RealtimeChannel | null;

  initializeSprints: () => Promise<void>;
  refreshSprints: () => Promise<void>;
  refreshSprintsIfStale: () => Promise<void>;
  setupRealtimeSubscription: () => Promise<void>;
  cleanupRealtimeSubscription: () => void;
  setSelectedTeamId: (teamId: string | null) => void;
  setSprintDisplayMode: (mode: 'list' | 'board') => void;
  setSelectedSprintId: (sprintId: string | null) => void;

  // ✅ allow goals + optional project on create
  addSprint: (data: {
    name: string;
    startDate: string;
    endDate: string;
    goals?: string;
    projectId?: string | null;
    teamId?: string | null;
  }) => Promise<Sprint | null>;

  // ✅ include goals + projectId on update
  updateSprint: (
    sprintId: string,
    data: {
      name: string;
      startDate: string;
      endDate: string;
      tasks: string[];
      teamId: string | null;
      goals: string;
      projectId?: string | null;
    }
  ) => Promise<Sprint | null>;

  deleteSprint: (sprintId: string) => Promise<void>;
  archiveSprint: (sprintId: string, archived: boolean) => Promise<void>;
  voteOnSprint: (sprintId: string, userId: string, voteType: 'up' | 'down') => Promise<void>;
  getUserVote: (sprintId: string, userId: string) => 'up' | 'down' | null;
  startSprint: (sprintId: string) => Promise<void>;
  completeSprint: (sprintId: string) => Promise<void>;
  pauseSprint: (sprintId: string, reason: string) => Promise<void>;
  resumeSprint: (sprintId: string) => Promise<void>;
  getSprintById: (sprintId: string) => Sprint | undefined;
}

const useSprintStore = create<SprintState>((set, get) => ({
  sprints: [],
  isLoading: false,
  error: null,
  selectedTeamId: null,
  sprintDisplayMode: 'list',
  selectedSprintId: null,
  lastFetchedAt: null,
  realtimeChannel: null,

  initializeSprints: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;

      if (!currentUser?.companyId) {
        console.warn('initializeSprints: No current user companyId found. Skipping sprint initialization.');
        set({ sprints: [], isLoading: false });
        return;
      }

      // Full company set; TeamFilterChip filters per view.
      const { sprints } = await fetchSprints(currentUser.companyId, null);

      const savedDisplayMode = localStorage.getItem('sprintDisplayMode');
      const initialDisplayMode: 'list' | 'board' =
        savedDisplayMode === 'list' || savedDisplayMode === 'board' ? savedDisplayMode : 'list';

      const savedSelectedSprintId = localStorage.getItem('selectedSprintId');
      const initialSelectedSprintId = sprints.find((s) => s.id === savedSelectedSprintId)?.id || null;

      set({
        sprints,
        sprintDisplayMode: initialDisplayMode,
        selectedSprintId: initialSelectedSprintId,
        isLoading: false,
        lastFetchedAt: Date.now(),
      });
      console.log('SprintStore: Sprints initialized with IDs:', get().sprints.map((s) => s.id));
    } catch (error: any) {
      console.error('Error loading sprints:', error);
      set({ error: `Failed to load sprints: ${error.message}`, isLoading: false });
    }
  },

  setSelectedTeamId: (teamId) => {
    set({ selectedTeamId: teamId });
    get().initializeSprints();
  },

  refreshSprints: async () => {
    // Refetch sprints without changing UI state (like selected sprint)
    const currentUser = useUserStore.getState().currentUser;

    if (!currentUser?.companyId) {
      console.warn('refreshSprints: No current user companyId found.');
      return;
    }

    const { sprints } = await fetchSprints(currentUser.companyId, null);

    set({ sprints, lastFetchedAt: Date.now() });
    console.log('SprintStore: Sprints refreshed from DB');
  },

  refreshSprintsIfStale: async () => {
    const { lastFetchedAt, isLoading, sprints } = get();
    const now = Date.now();
    const STALE_THRESHOLD = 30000; // 30 seconds

    // If no sprints loaded yet, initialize
    if (sprints.length === 0) {
      await get().initializeSprints();
      return;
    }

    // Skip if already loading or data is fresh
    if (isLoading || (lastFetchedAt && now - lastFetchedAt < STALE_THRESHOLD)) {
      console.log('refreshSprintsIfStale: Data is fresh, skipping refresh');
      return;
    }

    console.log('refreshSprintsIfStale: Data is stale, refreshing...');
    await get().refreshSprints();
  },

  /**
   * Setup realtime subscription for sprints
   */
  setupRealtimeSubscription: async () => {
    // Clean up existing subscription
    get().cleanupRealtimeSubscription();

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupRealtimeSubscription: No current user companyId found. Skipping realtime subscription setup.');
      return;
    }

    // Ensure sprints are loaded first
    const { sprints, isLoading } = get();
    if (sprints.length === 0 && !isLoading) {
      await get().initializeSprints();
    }

    const channel = supabase
      .channel('sprints_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sprints',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        (payload) => {
          console.log('Real-time sprint payload received:', payload);
          const { eventType, new: newRow, old: oldRow } = payload;

          switch (eventType) {
            case 'INSERT': {
              const newSprint = mapSprintFromDB(newRow as any);
              // Dedupe: streamStore's agent-object handler may have inserted it already
              set((state) =>
                state.sprints.some((s) => s.id === newSprint.id)
                  ? state
                  : { sprints: [newSprint, ...state.sprints] }
              );
              break;
            }

            case 'UPDATE': {
              // Preserve in-memory tasks — realtime payload is the sprint row only.
              set((state) => ({
                sprints: state.sprints.map((s) => {
                  if (s.id !== (newRow as { id?: string }).id) return s;
                  return {
                    ...mapSprintFromDB(newRow as any, s.tasks),
                    tasks: s.tasks,
                  };
                }),
              }));
              break;
            }

            case 'DELETE': {
              set((state) => ({
                sprints: state.sprints.filter((s) => s.id !== oldRow.id),
                selectedSprintId:
                  state.selectedSprintId === oldRow.id ? null : state.selectedSprintId,
              }));
              break;
            }
          }
        }
      )
      .subscribe();

    set({ realtimeChannel: channel });
    console.log('Sprints realtime subscription established');
  },

  /**
   * Cleanup realtime subscription
   */
  cleanupRealtimeSubscription: () => {
    const { realtimeChannel } = get();
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      set({ realtimeChannel: null });
      console.log('Sprints realtime subscription cleaned up');
    }
  },

  setSprintDisplayMode: (mode) => {
    set({ sprintDisplayMode: mode });
    localStorage.setItem('sprintDisplayMode', mode);
  },

  setSelectedSprintId: (sprintId) => {
    set({ selectedSprintId: sprintId });
    if (sprintId) {
      localStorage.setItem('selectedSprintId', sprintId);
    } else {
      localStorage.removeItem('selectedSprintId');
    }
  },

  addSprint: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const sprintData = mapSprintToDB({
        ...data, // may include optional goals + projectId
        status: 'planned',
        sentimentVotes: { up: 0, down: 0, user_votes: [] },
        teamId: data.teamId ?? currentUser.team ?? undefined,
        projectId: data.projectId ?? undefined,
        companyId: currentUser.companyId,
        createdById: currentUser.id,   // ✅ persist the creator
        requestedById: currentUser.id, // ✅ allow delegate to edit via RLS
      });

      const newSprint = await insertSprint(sprintData);

      const mappedSprint = {
        ...mapSprintFromDB(newSprint),
        tasks: [],
      };

      // Sprint creation doesn't generate a feed event (only start/complete/pause/resume do)

      set((state) => ({
        sprints: [mappedSprint, ...state.sprints],
        isLoading: false,
      }));
      return mappedSprint;
    } catch (error: any) {
      console.error('Error adding sprint:', error);
      set({ error: `Failed to add sprint: ${error.message}`, isLoading: false });
      return null;
    }
  },

  updateSprint: async (sprintId, data) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const oldSprint = get().sprints.find((s) => s.id === sprintId);
      if (!oldSprint) throw new Error('Sprint not found');

      const changes: string[] = [];

      if (oldSprint.name !== data.name) {
        changes.push(`name changed from "${oldSprint.name}" to "${data.name}"`);
      }

      const oldStartDateFormatted = format(new Date(oldSprint.startDate), 'MMM dd, yyyy');
      const newStartDateFormatted = format(new Date(data.startDate), 'MMM dd, yyyy');
      if (oldStartDateFormatted !== newStartDateFormatted) {
        changes.push(`start date changed from ${oldStartDateFormatted} to ${newStartDateFormatted}`);
      }

      const oldEndDateFormatted = format(new Date(oldSprint.endDate), 'MMM dd, yyyy');
      const newEndDateFormatted = format(new Date(data.endDate), 'MMM dd, yyyy');
      if (oldEndDateFormatted !== newEndDateFormatted) {
        changes.push(`end date changed from ${oldEndDateFormatted} to ${newEndDateFormatted}`);
      }

      // ✅ detect goals change
      if ((oldSprint.goals || '') !== (data.goals || '')) {
        changes.push('goals updated');
      }

      const allCards = useCardStore.getState().cards;
      const getCardTitle = (id: string) => {
        const card = allCards.find((c) => c.id === id);
        return card ? `"${card.title}"` : `unknown task (${id})`;
      };

      const addedTasks = data.tasks.filter((id) => !oldSprint.tasks.includes(id));
      const removedTasks = oldSprint.tasks.filter((id) => !data.tasks.includes(id));

      if (addedTasks.length > 0) {
        changes.push(`added task(s): ${addedTasks.map(getCardTitle).join(', ')}`);
      }
      if (removedTasks.length > 0) {
        changes.push(`removed task(s): ${removedTasks.map(getCardTitle).join(', ')}`);
      }

      let description = `${currentUser.name} updated sprint "${oldSprint.name}"`;
      if (changes.length > 0) {
        description += `: ${changes.join(', ')}.`;
      } else {
        description += `.`;
      }

      const sprintUpdates: Partial<Sprint> = {
        name: data.name,
        startDate: data.startDate,
        endDate: data.endDate,
        teamId: data.teamId || undefined,
        companyId: currentUser.companyId,
        goals: data.goals, // ✅ include goals so it persists
      };
      // Keep null so mapSprintUpdatesToDB can clear project_id (|| undefined would drop the key).
      if (data.projectId !== undefined) {
        sprintUpdates.projectId = data.projectId;
      }

      const updatedSprintRow = await updateSprintById(sprintId, sprintUpdates, data.tasks);

      const mappedSprint = {
        ...mapSprintFromDB(updatedSprintRow),
        tasks: data.tasks,
      };

      // Sprint updates don't generate feed events (only status changes like start/complete/pause/resume do)

      set((state) => ({
        sprints: state.sprints.map((sprint) => (sprint.id === sprintId ? mappedSprint : sprint)),
        isLoading: false,
      }));
      console.log('SprintStore: Sprint updated. Current sprint IDs:', get().sprints.map((s) => s.id));
      
      // If sprint was linked to a project, refresh cards (ticket numbers / inherited project_id).
      if (data.projectId !== undefined) {
        useCardStore.getState().initializeCards();
      }
      
      return mappedSprint;
    } catch (error: any) {
      console.error('Error updating sprint:', error);
      set({ error: `Failed to update sprint: ${error.message}`, isLoading: false });
      return null;
    }
  },

  deleteSprint: async (sprintId) => {
    const t0 = performance.now();
    console.log('[Sprint delete] Store: starting', { sprintId });
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const sprint = get().sprints.find((s) => s.id === sprintId);
      if (!sprint) throw new Error('Sprint not found');
      console.log('[Sprint delete] Store: calling deleteSprintById', { sprintId, name: sprint.name });

      await deleteSprintById(sprintId);
      console.log('[Sprint delete] Store: deleteSprintById returned', { sprintId, ms: Math.round(performance.now() - t0) });

      // Sprint deletion doesn't generate a feed event

      set((state) => ({
        sprints: state.sprints.filter((s) => s.id !== sprintId),
        isLoading: false,
      }));
      console.log('[Sprint delete] Store: state updated, sprint removed from list', { sprintId, ms: Math.round(performance.now() - t0) });
    } catch (error: any) {
      console.error('[Sprint delete] Store: error', { sprintId, error: error?.message ?? error });
      set({ error: `Failed to delete sprint: ${error.message}`, isLoading: false });
    }
  },

  archiveSprint: async (sprintId, archived) => {
    const sprint = get().sprints.find((s) => s.id === sprintId);
    if (!sprint) return;
    const prior = sprint.status && sprint.status !== 'archived' ? sprint.status : 'completed';
    const nextStatus = archived ? 'archived' : (sprint.statusReason?.startsWith('prior:')
      ? sprint.statusReason.slice(6) as Sprint['status']
      : prior);
    const row = await updateSprintById(sprintId, {
      status: archived ? 'archived' : (nextStatus === 'archived' ? 'completed' : nextStatus),
      statusReason: archived ? `prior:${prior}` : '',
    });
    const mapped = { ...mapSprintFromDB(row), tasks: sprint.tasks };
    set((state) => ({
      sprints: state.sprints.map((s) => (s.id === sprintId ? mapped : s)),
    }));
  },

  voteOnSprint: async (sprintId, userId, voteType) => {
    try {
      const sprint = get().sprints.find((s) => s.id === sprintId);
      if (!sprint) return;

      const newVotes = { ...sprint.sentimentVotes };
      const userVotes = [...newVotes.user_votes];

      const existingVoteIndex = userVotes.findIndex((vote) => vote.user_id === userId);
      const existingVote = existingVoteIndex >= 0 ? userVotes[existingVoteIndex] : null;

      if (existingVote) {
        if (existingVote.type === voteType) {
          newVotes[voteType]--;
          userVotes.splice(existingVoteIndex, 1);
        } else {
          newVotes[existingVote.type]--;
          newVotes[voteType]++;
          userVotes[existingVoteIndex] = { user_id: userId, type: voteType };
        }
      } else {
        newVotes[voteType]++;
        userVotes.push({ user_id: userId, type: voteType });
      }

      newVotes.user_votes = userVotes;

      set((state) => ({
        sprints: state.sprints.map((s) => (s.id === sprintId ? { ...s, sentimentVotes: newVotes } : s)),
      }));

      const updatedSprintRow = await updateSprintById(sprintId, { sentimentVotes: newVotes });

      const updatedSprint = {
        ...mapSprintFromDB(updatedSprintRow),
        tasks: sprint.tasks,
      };

      const currentUser = useUserStore.getState().currentUser;
      if (currentUser) {
        useFeedStore.getState().addEvent({
          type: 'sprint_voted',
          title: 'Sprint Voted',
          description: `${currentUser.name} voted on sprint "${sprint.name}"`,
          userId: currentUser.id,
          initiatorId: currentUser.id,
          sprintId: updatedSprint.id,
        });
      }

      set((state) => ({
        sprints: state.sprints.map((s) => (s.id === sprintId ? updatedSprint : s)),
      }));
    } catch (error: any) {
      console.error('Error voting on sprint:', error);
      set({ error: `Failed to vote on sprint: ${error.message}` });
    }
  },

  getUserVote: (sprintId, userId) => {
    const sprint = get().sprints.find((s) => s.id === sprintId);
    if (!sprint) return null;

    const userVote = sprint.sentimentVotes.user_votes.find((vote) => vote.user_id === userId);
    return userVote ? userVote.type : null;
  },

  startSprint: async (sprintId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const sprint = get().sprints.find((s) => s.id === sprintId);
      if (!sprint) throw new Error('Sprint not found');

      const updatedSprintRow = await updateSprintById(sprintId, { status: 'active' });

      const mappedSprint = {
        ...mapSprintFromDB(updatedSprintRow),
        tasks: sprint.tasks,
      };

      useFeedStore.getState().addEvent({
        type: 'sprint_started',
        title: 'Sprint Started',
        description: `${currentUser.name} started sprint "${sprint.name}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        sprintId: sprint.id,
        projectId: mappedSprint.projectId ?? sprint.projectId ?? undefined,
        roadmapId: mappedSprint.roadmapId ?? sprint.roadmapId ?? undefined,
      });

      try {
        await useFollowStore.getState().notifyFollowers(
          followRefsFromSprint(mappedSprint),
          [currentUser.id],
          {
            type: 'sprint_started',
            title: 'Sprint started',
            description: `${currentUser.name} started sprint "${sprint.name}"`,
            initiatorId: currentUser.id,
            sprintId: sprint.id,
            projectId: mappedSprint.projectId ?? sprint.projectId ?? undefined,
            roadmapId: mappedSprint.roadmapId ?? sprint.roadmapId ?? undefined,
          },
        );
      } catch (followErr) {
        console.warn('Follower notify on sprint start failed (non-critical):', followErr);
      }

      set((state) => ({
        sprints: state.sprints.map((s) => (s.id === sprintId ? mappedSprint : s)),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error starting sprint:', error);
      set({ error: `Failed to start sprint: ${error.message}`, isLoading: false });
    }
  },

  completeSprint: async (sprintId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const sprint = get().sprints.find((s) => s.id === sprintId);
      if (!sprint) throw new Error('Sprint not found');

      const updatedSprintRow = await updateSprintById(sprintId, { status: 'completed' });

      const mappedSprint = {
        ...mapSprintFromDB(updatedSprintRow),
        tasks: sprint.tasks,
      };

      useFeedStore.getState().addEvent({
        type: 'sprint_completed',
        title: 'Sprint Completed',
        description: `${currentUser.name} completed sprint "${sprint.name}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        sprintId: sprint.id,
        projectId: mappedSprint.projectId ?? sprint.projectId ?? undefined,
        roadmapId: mappedSprint.roadmapId ?? sprint.roadmapId ?? undefined,
      });

      try {
        await useFollowStore.getState().notifyFollowers(
          followRefsFromSprint(mappedSprint),
          [currentUser.id],
          {
            type: 'sprint_completed',
            title: 'Sprint completed',
            description: `${currentUser.name} completed sprint "${sprint.name}"`,
            initiatorId: currentUser.id,
            sprintId: sprint.id,
            projectId: mappedSprint.projectId ?? sprint.projectId ?? undefined,
            roadmapId: mappedSprint.roadmapId ?? sprint.roadmapId ?? undefined,
          },
        );
      } catch (followErr) {
        console.warn('Follower notify on sprint complete failed (non-critical):', followErr);
      }

      set((state) => ({
        sprints: state.sprints.map((s) => (s.id === sprintId ? mappedSprint : s)),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error completing sprint:', error);
      set({ error: `Failed to complete sprint: ${error.message}`, isLoading: false });
    }
  },

  pauseSprint: async (sprintId, reason) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const sprint = get().sprints.find((s) => s.id === sprintId);
      if (!sprint) throw new Error('Sprint not found');

      const now = new Date().toISOString();
      const updatePayload = { 
        pausedAt: now,
        pausedReason: reason,
        pausedById: currentUser.id,
      };
      const updatedSprintRow = await updateSprintById(sprintId, updatePayload);

      const mappedSprint = {
        ...mapSprintFromDB(updatedSprintRow),
        tasks: sprint.tasks,
      };

      try {
        await useFeedStore.getState().addEvent({
          type: 'sprint_paused',
          title: 'Sprint Paused',
          description: `${currentUser.name} paused sprint "${sprint.name}": ${reason}`,
          userId: undefined, // Broadcast event, not user-specific
          initiatorId: currentUser.id,
          sprintId: sprint.id,
        });
      } catch (feedError) {
        console.error('❌ Error creating sprint_paused feed event:', feedError);
      }

      set((state) => ({
        sprints: state.sprints.map((s) => (s.id === sprintId ? mappedSprint : s)),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error pausing sprint:', error);
      set({ error: `Failed to pause sprint: ${error.message}`, isLoading: false });
    }
  },

  resumeSprint: async (sprintId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const sprint = get().sprints.find((s) => s.id === sprintId);
      if (!sprint) throw new Error('Sprint not found');

      const now = new Date().toISOString();
      const updatedSprintRow = await updateSprintById(sprintId, { 
        resumedAt: now,
        resumedById: currentUser.id,
      });

      const mappedSprint = {
        ...mapSprintFromDB(updatedSprintRow),
        tasks: sprint.tasks,
      };

      try {
        await useFeedStore.getState().addEvent({
          type: 'sprint_resumed',
          title: 'Sprint Resumed',
          description: `${currentUser.name} resumed sprint "${sprint.name}"`,
          userId: undefined, // Broadcast event, not user-specific
          initiatorId: currentUser.id,
          sprintId: sprint.id,
        });
      } catch (feedError) {
        console.error('❌ Error creating sprint_resumed feed event:', feedError);
      }

      set((state) => ({
        sprints: state.sprints.map((s) => (s.id === sprintId ? mappedSprint : s)),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error resuming sprint:', error);
      set({ error: `Failed to resume sprint: ${error.message}`, isLoading: false });
    }
  },

  getSprintById: (sprintId: string) => {
    return get().sprints.find((sprint) => sprint.id === sprintId);
  },
}));

export default useSprintStore;
